import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Download, CreditCard, Loader2, CheckCircle } from 'lucide-react';
import { api } from '../../api/client';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { isNative } from '../../lib/platform';
import PortalLayout from './PortalLayout';
// invoice_date / quote_date / due_date / payment_date are INSTANTS (api#838)
// and render as the TENANT's day via formatInstantTenantDay — their UTC
// prefix read a day late for anything written after 18:00 MDT, on the
// screen the CUSTOMER reads. valid_until is a calendar day and stays on
// formatCalendarDate.
//
// Before that they were treated as calendar days: `new Date()` read them as
// UTC midnight and this portal showed every one of them a day early. The local
// fmtDate that fixed that then ended in a bare toLocaleDateString(), so the
// FORMAT tracked the reader's own device instead: one customer saw 3/23/2026
// and another 23/03/2026 for the same day, with nothing on the page to say
// which. formatCalendarDate's default renders a month NAME, which no device
// can reorder into a different day.
import { formatInstantTenantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency, fmtUnitRate } from '../../lib/display-labels';

export default function PortalInvoiceDetail() {
  const timeZone = useTenantTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentSuccess] = useState(searchParams.get('payment') === 'success');
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-invoice', id],
    queryFn: () => api.portal.invoice(id!).then(r => r.data.data),
    enabled: !!id,
  });

  const payMutation = useMutation({
    mutationFn: () => api.portal.payInvoice(id!),
    onSuccess: (res) => {
      const url = res.data?.data?.checkoutUrl;
      if (url) window.location.href = url;
    },
  });

  const [saved, setSaved] = useState(false);

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]); // strip data:...;base64, prefix
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handleDownload = async () => {
    setDownloading(true);
    setSaved(false);
    try {
      const res = await api.portal.invoicePdf(id!);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const filename = `${data?.invoice?.invoice_number || 'invoice'}.pdf`;

      if (isNative) {
        const base64 = await blobToBase64(blob);
        await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Documents,
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('PDF download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  // Money on a document the CUSTOMER reads. `parseFloat(n || '0')` turned an
  // amount the server did not send into $0.00 — an invoice total that is
  // genuinely unknown is not an invoice for nothing. @see fmtCurrency.
  const fmt = (n: number | string | null | undefined) =>
    fmtCurrency(n, data?.invoice?.currency || 'CAD');
  const invoice = data?.invoice;
  const lineItems = data?.lineItems || [];
  const payments = data?.payments || [];
  const balanceDue = parseFloat(invoice?.balance_due || '0');

  return (
    <PortalLayout>
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-fieldiq-accent" />
        </div>
      ) : !invoice ? (
        <p className="text-center text-fieldiq-muted py-20">Invoice not found</p>
      ) : (
        <div className="space-y-4 animate-fade-in">
          {/* Back + Header */}
          <button onClick={() => navigate('/portal/invoices')} className="flex items-center gap-1 text-fieldiq-muted hover:text-white text-sm">
            <ArrowLeft size={26} /> Back to Invoices
          </button>

          {paymentSuccess && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle size={24} className="text-green-400" />
              <div>
                <p className="text-green-400 font-semibold text-sm">Payment Successful</p>
                <p className="text-green-400/70 text-xs">Your payment has been processed. It may take a moment to reflect.</p>
              </div>
            </div>
          )}

          {/* Invoice Header */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-white font-bold text-xl">{invoice.invoice_number}</h2>
                <p className="text-fieldiq-muted text-sm mt-1">
                  Issued: {(formatInstantTenantDay(invoice.invoice_date, timeZone) ?? '—')} · Due: {(formatInstantTenantDay(invoice.due_date, timeZone) ?? '—')}
                </p>
              </div>
              <StatusBadge status={invoice.status} />
            </div>

            {/* From / To */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-fieldiq-muted text-xs mb-1">From</p>
                <p className="text-white font-medium">{invoice.company_name}</p>
                {invoice.company_address && <p className="text-fieldiq-muted text-xs">{invoice.company_address}</p>}
              </div>
              <div>
                <p className="text-fieldiq-muted text-xs mb-1">To</p>
                <p className="text-white font-medium">{invoice.customer_name}</p>
                {invoice.customer_address && <p className="text-fieldiq-muted text-xs">{invoice.customer_address}</p>}
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
            <h3 className="text-white font-semibold text-sm mb-3">Line Items</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fieldiq-muted text-xs border-b border-fieldiq-border">
                    <th className="text-left py-2 pr-4">Description</th>
                    <th className="text-right py-2 px-2">Qty</th>
                    <th className="text-right py-2 px-2">Rate</th>
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li: any, i: number) => (
                    <tr key={li.id || i} className="border-b border-fieldiq-border/50">
                      <td className="py-2.5 pr-4 text-white">{li.description || li.line_item_type}</td>
                      <td className="py-2.5 px-2 text-right text-fieldiq-muted">{li.quantity}</td>
                      <td className="py-2.5 px-2 text-right text-fieldiq-muted">{fmtUnitRate(li.rate, data?.invoice?.currency || 'CAD')}</td>
                      <td className="py-2.5 text-right text-white font-medium">{fmt(li.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-4 pt-3 border-t border-fieldiq-border space-y-1.5 text-sm">
              <div className="flex justify-between text-fieldiq-muted">
                <span>Subtotal</span>
                <span>{fmt(invoice.subtotal)}</span>
              </div>
              {invoice.tax_rate_1_amount && parseFloat(invoice.tax_rate_1_amount) > 0 && (
                <div className="flex justify-between text-fieldiq-muted">
                  <span>{invoice.tax_rate_1_label || 'Tax'}</span>
                  <span>{fmt(invoice.tax_rate_1_amount)}</span>
                </div>
              )}
              {invoice.tax_rate_2_amount && parseFloat(invoice.tax_rate_2_amount) > 0 && (
                <div className="flex justify-between text-fieldiq-muted">
                  <span>{invoice.tax_rate_2_label || 'Tax'}</span>
                  <span>{fmt(invoice.tax_rate_2_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-white font-bold text-base pt-1">
                <span>Total</span>
                <span>{fmt(invoice.total_amount)}</span>
              </div>
              {parseFloat(invoice.amount_paid || '0') > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Paid</span>
                  <span>-{fmt(invoice.amount_paid)}</span>
                </div>
              )}
              <div className="flex justify-between text-white font-bold text-lg pt-1 border-t border-fieldiq-border">
                <span>Balance Due</span>
                <span>{fmt(invoice.balance_due)}</span>
              </div>
            </div>
          </div>

          {/* Payments */}
          {payments.length > 0 && (
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
              <h3 className="text-white font-semibold text-sm mb-3">Payment History</h3>
              <div className="space-y-2">
                {payments.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between py-2 border-b border-fieldiq-border/50 last:border-0">
                    <div>
                      <p className="text-white text-sm">{(formatInstantTenantDay(p.payment_date, timeZone) ?? '—')}</p>
                      <p className="text-fieldiq-muted text-xs capitalize">{(p.payment_method || '').replace('_', ' ')}</p>
                    </div>
                    <p className="text-green-400 font-semibold text-sm">{fmt(p.amount)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-fieldiq-card border border-fieldiq-border text-white font-semibold rounded-xl text-sm hover:bg-fieldiq-card/80 transition-colors disabled:opacity-50"
            >
              {downloading ? <Loader2 size={16} className="animate-spin" /> : saved ? <CheckCircle size={16} /> : <Download size={16} />}
              {downloading ? 'Downloading...' : saved ? 'Saved to Documents' : 'Download PDF'}
            </button>
            {balanceDue > 0 && invoice.status !== 'paid' && invoice.status !== 'void' && (
              <button
                onClick={() => payMutation.mutate()}
                disabled={payMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-fieldiq-accent text-black font-bold rounded-xl text-sm hover:bg-fieldiq-accent/90 transition-colors disabled:opacity-50"
              >
                {payMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {payMutation.isPending ? 'Processing...' : `Pay Now ${fmt(String(balanceDue))}`}
              </button>
            )}
          </div>

          {payMutation.isError && (
            <p className="text-red-400 text-sm text-center">
              {(payMutation.error as any)?.response?.data?.error || 'Payment failed. Please try again.'}
            </p>
          )}
        </div>
      )}
    </PortalLayout>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    sent: 'bg-blue-500/20 text-blue-400',
    paid: 'bg-green-500/20 text-green-400',
    overdue: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status?.toUpperCase()}
    </span>
  );
}
